import {
  EMableCustomEventName,
  EMableEventName,
} from '@/core/constants/event_types/events';

export type GA4Item = {
  affiliation?: string;
  coupon?: string;
  creative_name?: string;
  creative_slot?: string;
  currency?: string;
  discount?: number;
  index?: number;
  // Enhanced product details
  item_brand?: string;
  item_category?: string;

  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  // Basic item parameters
  item_id: string;
  // List parameters
  item_list_id?: string;
  item_list_name?: string;

  item_name?: string;
  item_variant?: string;
  // Location information
  location_id?: string;

  price?: number;
  // Product identifiers
  product_id?: string;

  product_number?: string;
  // Promotion parameters
  promotion_id?: string;
  promotion_name?: string;
  quantity?: number;

  variant_id?: string;
};

/**
 * GA4 Event Parameters interface
 * Contains common parameters for all GA4 events
 */
export type GA4EventParameters = {
  // Allow for any additional custom parameters
  [key: string]: any;

  browser?: string;
  browser_version?: string;
  checkout_option?: string;
  // Checkout parameters
  checkout_step?: number;
  coupon?: string;

  creative_name?: string;
  creative_slot?: string;
  // Ecommerce parameters
  currency?: string;
  // Customer data
  customer_id?: string;
  customer_type?: string;
  device_category?: string;
  discount?: number;
  discount_amount?: number;
  // Engagement metrics
  engagement_time_msec?: number;
  // Standard event parameters
  event_id?: string;

  fbclid?: string;
  // Form parameters
  form_id?: string;
  form_name?: string;

  gacid?: string;
  // Campaign data
  gclid?: string;
  ip_address?: string;
  // List parameters
  item_list_id?: string;
  item_list_name?: string;

  // Items array for retail events
  items?: GA4Item[];
  language?: string;
  // Lead parameters
  lead_source?: string;
  method?: string;
  num_items?: number;
  operating_system?: string;
  operating_system_version?: string;
  // Conversion value data
  original_value?: number;
  // Page metadata
  page_category?: string;
  page_location?: string;

  page_name?: string;
  page_referrer?: string;
  page_section?: string;
  page_title?: string;

  page_type?: string;
  pay_methods?: string[];
  payment_type?: string;
  // Promotion parameters
  promotion_id?: string;
  promotion_name?: string;
  screen_resolution?: string;
  // Search parameters
  search_term?: string;
  // Session data
  session_id?: string;
  session_number?: number;
  shipping?: number;
  shipping_amount?: number;
  shipping_tier?: string;
  tax?: number;
  tax_amount?: number;
  transaction_id?: string;
  ttclid?: string;

  user_address?: string;
  // User context
  user_agent?: string;
  user_city?: string;
  user_country?: string;

  // User data for enhanced conversions
  user_email?: string;
  user_email_hash?: string;

  user_first_name?: string;

  user_first_name_hash?: string;
  user_id?: string;

  user_language?: string;
  user_last_name?: string;
  user_last_name_hash?: string;
  user_lifetime_value?: number;

  user_phone?: string;
  user_phone_hash?: string;
  user_postal_code?: string;

  user_region?: string;
  user_state?: string;

  user_zip?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_medium?: string;

  // UTM parameters
  utm_source?: string;
  utm_term?: string;

  value?: number;

  viewport_size?: string;
};

export type GA4Event = {
  custom_event_name?: string;
  event_name: string;
  event_params: GA4EventParameters;
  user_data?: any;
};

/**
 * Enum for GA4 standard event names
 */
export enum GA4PayloadEventName {
  ADD_PAYMENT_INFO = 'add_payment_info',
  ADD_SHIPPING_INFO = 'add_shipping_info',
  ADD_TO_CART = 'add_to_cart',
  ADD_TO_WISHLIST = 'add_to_wishlist',
  BEGIN_CHECKOUT = 'begin_checkout',
  CUSTOM = 'custom',
  // Custom events for specific business needs
  CUSTOM_ALL_CUSTOMER_PURCHASE = 'all_customer_purchase',
  CUSTOM_NEW_CUSTOMER_PURCHASE = 'new_customer_purchase',
  CUSTOM_PAGE_VIEW = 'custom_page_view',
  CUSTOM_RETURNING_CUSTOMER_PURCHASE = 'returning_customer_purchase',
  EARN_VIRTUAL_CURRENCY = 'earn_virtual_currency',
  GENERATE_LEAD = 'generate_lead',
  JOIN_GROUP = 'join_group',
  LEVEL_END = 'level_end',
  LEVEL_START = 'level_start',
  LEVEL_UP = 'level_up',
  LOGIN = 'login',
  PAGE_VIEW = 'page_view',
  POST = 'post',
  PURCHASE = 'purchase',
  REFUND = 'refund',
  REMOVE_FROM_CART = 'remove_from_cart',
  SEARCH = 'search',
  SELECT_CONTENT = 'select_content',
  SELECT_ITEM = 'select_item',
  SELECT_PROMOTION = 'select_promotion',
  SHARE = 'share',
  SIGN_UP = 'sign_up',
  SPEND_VIRTUAL_CURRENCY = 'spend_virtual_currency',
  TUTORIAL_BEGIN = 'tutorial_begin',
  TUTORIAL_COMPLETE = 'tutorial_complete',
  UNLOCK_ACHIEVEMENT = 'unlock_achievement',
  VIEW_CART = 'view_cart',

  VIEW_ITEM = 'view_item',
  VIEW_ITEM_LIST = 'view_item_list',
  VIEW_PROMOTION = 'view_promotion',
  VIEW_SEARCH_RESULTS = 'view_search_results',
}

/**
 * Events that typically include cart data
 */
export const EventsWithCartData = [
  EMableEventName.CHECKOUT_STARTED,
  EMableEventName.PAYMENT_INFO_ADDED,
];

export const EventsWithProductData: EMableEventName[] = [
  EMableEventName.PRODUCT_ADDED,
  EMableEventName.PRODUCT_VIEWED,
  EMableEventName.CHECKOUT_STARTED,
  EMableEventName.COLLECTION_VIEWED,
  EMableEventName.PAYMENT_INFO_ADDED,
];

/**
 * Type for supported Mable event names that can be mapped to GA4 events
 */
export type TSupportedGA4EventNames =
  | EMableCustomEventName.AllCustomerPurchase
  | EMableCustomEventName.NewCustomerPurchase
  | EMableCustomEventName.ReturningCustomerPurchase
  | EMableEventName.CHECKOUT_STARTED
  | EMableEventName.COLLECTION_VIEWED
  | EMableEventName.CUSTOM_EVENT
  | EMableEventName.LEAD
  | EMableEventName.ORDER_COMPLETED
  | EMableEventName.PAGE_VIEWED
  | EMableEventName.PAYMENT_INFO_ADDED
  | EMableEventName.PRODUCT_ADDED
  | EMableEventName.PRODUCT_VIEWED;

/**
 * Mapping from Mable event names to GA4 event names
 */
export const mableToGA4EventMappings: Record<
  TSupportedGA4EventNames,
  GA4PayloadEventName
> = {
  [EMableCustomEventName.AllCustomerPurchase]:
    GA4PayloadEventName.CUSTOM_ALL_CUSTOMER_PURCHASE,
  [EMableCustomEventName.NewCustomerPurchase]:
    GA4PayloadEventName.CUSTOM_NEW_CUSTOMER_PURCHASE,
  [EMableCustomEventName.ReturningCustomerPurchase]:
    GA4PayloadEventName.CUSTOM_RETURNING_CUSTOMER_PURCHASE,
  [EMableEventName.CHECKOUT_STARTED]: GA4PayloadEventName.BEGIN_CHECKOUT,
  [EMableEventName.COLLECTION_VIEWED]: GA4PayloadEventName.VIEW_ITEM_LIST,
  [EMableEventName.CUSTOM_EVENT]: GA4PayloadEventName.CUSTOM,
  [EMableEventName.LEAD]: GA4PayloadEventName.GENERATE_LEAD,
  [EMableEventName.ORDER_COMPLETED]: GA4PayloadEventName.PURCHASE,
  [EMableEventName.PAGE_VIEWED]: GA4PayloadEventName.CUSTOM_PAGE_VIEW,
  [EMableEventName.PAYMENT_INFO_ADDED]: GA4PayloadEventName.ADD_PAYMENT_INFO,
  [EMableEventName.PRODUCT_ADDED]: GA4PayloadEventName.ADD_TO_CART,
  [EMableEventName.PRODUCT_VIEWED]: GA4PayloadEventName.VIEW_ITEM,
};

/**
 * Calculate the GA4 event name from a Mable event name
 * @param eventName The Mable event name
 * @param customEventName Optional custom event name for CUSTOM_EVENT
 * @returns The mapped GA4 event name
 */
export const calculateGA4EventMapping = (
  eventName: EMableEventName,
  customEventName?: EMableCustomEventName | string,
): GA4PayloadEventName => {
  const name = eventName as TSupportedGA4EventNames;
  let mappedGA4EventName = mableToGA4EventMappings[name];

  if (name === EMableEventName.CUSTOM_EVENT && customEventName) {
    mappedGA4EventName =
      mableToGA4EventMappings[customEventName as TSupportedGA4EventNames];
  }

  return mappedGA4EventName;
};

/**
 * GA4 Enhanced Measurement Configuration
 */
export type GA4EnhancedMeasurementConfig = {
  file_downloads?: boolean;
  form_interactions?: boolean;
  outbound_links?: boolean;
  page_changes?: boolean;
  scroll_tracking?: boolean;
  site_search?: boolean;
  video_tracking?: boolean;
};

/**
 * GA4 User Properties
 */
export type GA4UserProperties = Record<
  string,
  {
    value: boolean | number | string;
  }
>;

/**
 * GA4 Consent Mode Configuration
 */
export type GA4ConsentConfig = {
  ad_personalization?: 'denied' | 'granted';
  ad_storage?: 'denied' | 'granted';
  ad_user_data?: 'denied' | 'granted';
  analytics_storage?: 'denied' | 'granted';
  functionality_storage?: 'denied' | 'granted';
  personalization_storage?: 'denied' | 'granted';
  security_storage?: 'denied' | 'granted';
};

/**
 * GA4 Configuration Options
 */
export type GA4ConfigOptions = {
  allow_ad_personalization_signals?: boolean;
  allow_google_signals?: boolean;
  consent?: GA4ConsentConfig;
  debug_mode?: boolean;
  enhanced_measurement?: GA4EnhancedMeasurementConfig;
  send_page_view?: boolean;
  transport_url?: string;
  user_properties?: GA4UserProperties;
};
